import React from 'react'
import { useSpring, animated } from 'react-spring'
import dDayCalculate, { dDayStateCalculate } from '@pocapan/shared/utils/dDayCalculate'

interface EventDDayBadgeProps {
  startDate: string
  endDate: string
  size?: 'small' | 'medium'
}

// 상태별 설정 타입 정의
interface BaseStateConfig {
  className: string
  useAnimation: boolean
}

interface AnimatedStateConfig extends BaseStateConfig {
  useAnimation: true
  animationStyle: React.CSSProperties
  isTextGradient?: boolean
}

interface StaticStateConfig extends BaseStateConfig {
  useAnimation: false
}

type StateConfig = StaticStateConfig | AnimatedStateConfig

function EventDDayBadge({ startDate, endDate, size = 'small' }: EventDDayBadgeProps) {
  const dDay = dDayCalculate(startDate, endDate)
  const dDayState = dDayStateCalculate(startDate, endDate)

  const BG_SHINE = useSpring({
    from: { backgroundPosition: '0% 50%' },
    to: { backgroundPosition: '100% 50%' },
    config: { duration: 2000 },
    loop: true
  })

  // size에 따른 조건부 클래스명
  const sizeClasses = size === 'small'
    ? 'text-[10px] leading-[12px] px-[5px] py-[2.5px]'
    : 'text-[13px] px-[8px] py-[4px]'

  // 공통 기본 클래스
  const baseClasses = `rounded-[6px] ${sizeClasses}`

  // 각 상태별 설정
  const stateConfig: Record<string, StateConfig> = {
    before: {
      className: `text-onSecondary-light dark:text-onSecondary-dark bg-secondary-light dark:bg-secondary-dark ${baseClasses}`,
      useAnimation: false
    },
    ongoing: {
      className: `text-pure-white bg-[#444444] ${baseClasses}`,
      useAnimation: true,
      animationStyle: {
        backgroundImage: 'linear-gradient(to right,#7953cd 20%,#00affa 30%,#0190cd 70%,#764ada 80%)',
        backgroundSize: '500% auto',
        whiteSpace: 'pre-wrap' as const
      }
    },
    endingToday: {
      className: `bg-separator-light-1 relative inline-block ${baseClasses}`,
      useAnimation: true,
      isTextGradient: true,
      animationStyle: {
        backgroundImage: 'linear-gradient(to right, #7953cd, #00affa, #0190cd, #764ada)',
        backgroundSize: '200% auto',
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        color: 'transparent'
      }
    },
    ended: {
      className: `text-onDisabled-light dark:text-onDisabled-dark bg-disabled-light dark:bg-disabled-dark ${baseClasses}`,
      useAnimation: false
    }
  }

  const config = stateConfig[dDayState]
  if (!config) return null

  // 텍스트 그라디언트용 내부 클래스
  const textGradientClasses = `text-pure-white flex items-center justify-center ${
    size === 'small' ? 'text-[10px] leading-[12px]' : 'text-[13px]'
  }`

  if (config.useAnimation) {
    const animatedConfig = config as AnimatedStateConfig
    const animationProps = {
      className: animatedConfig.isTextGradient ? textGradientClasses : animatedConfig.className,
      style: {
        ...BG_SHINE,
        ...animatedConfig.animationStyle
      },
      children: dDay
    }

    const AnimatedComponent = <animated.div {...animationProps} />

    // endingToday는 외부 wrapper가 필요
    if (animatedConfig.isTextGradient) {
      return <div className={animatedConfig.className}>{AnimatedComponent}</div>
    }

    return AnimatedComponent
  }

  // 애니메이션이 없는 경우
  return <div className={config.className}>{dDay}</div>
}

export default EventDDayBadge
